// ════════════════════════════════════════════════════════════════════════
//  Eine Lektion bearbeiten
//
//  Was hier NICHT liegt: die Finger-Zuordnung. Sie gilt für den ganzen Lehrgang
//  und steht in den Grundlagen – wer hier ein Zeichen benutzt, das dort fehlt,
//  bekommt den Hinweis an der Stufe und kommt mit einem Klick hin.
//
//  Schlüssel sind nach dem ersten Veröffentlichen unveränderlich; an ihnen
//  hängen die Fortschrittszeilen der Kinder. UMBENENNEN ist deshalb gesperrt,
//  sobald ein Schlüssel veröffentlicht ist.
//
//  ENTFERNEN hat die schärfere Bedingung, und zwar die schärfere in die andere
//  Richtung: gesperrt ist es erst, wenn ein Kind daran GEÜBT hat. Veröffentlicht
//  zu sein reicht nicht – ein Schlüssel ohne Ergebnis hat keine Sterne zu
//  verlieren, und ein Lehrgang entsteht nun einmal, während er unterrichtet
//  wird. Die Regel selbst steht als Trigger in der Datenbank; hier steht nur,
//  was der Bildschirm davon weiß.
//
//  Ohne offenen Entwurf zeigt dieselbe Seite die letzte veröffentlichte Fassung,
//  gesperrt (`readOnly`). Nachsehen, was in 4c steht, soll keinen Entwurf
//  brauchen – ein Entwurf ist ein Vorgang, kein Leserecht.
// ════════════════════════════════════════════════════════════════════════

import { Fragment, useState } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";
import type { Lesson, Stage } from "@otto/lehrgang";
import { formatKeys, parseKeys } from "../../../shared/lib/keys.js";
import { useDraft } from "../../../shared/context/DraftContext.js";
import { coursePath, useCourseSlug } from "../../../shared/lib/coursePath.js";
import { NoDraft } from "../../draft/components/NoDraft.js";
import { ConfirmDialog, TrashIcon } from "@otto/ui";
import { useAnchorFocus } from "../../../shared/hooks/useAnchorFocus.js";
import { ProblemList } from "../../validation/components/ProblemList.js";
import { problemsAt, problemsAtExcept } from "../../validation/lib/validation.js";
import { anchors } from "../../validation/lib/problemTarget.js";
import { StagePreview } from "../../preview/components/StagePreview.js";
import {
  insertStage,
  moveStage,
  moveStageTo,
  removeLesson,
  removeStage,
  removedButInUse,
  restoreLesson,
  restoreStage,
} from "../lib/edit.js";
import styles from "../../../shared/components/shell.module.css";
import own from "./lessons.module.css";

/**
 * Eine offene Rückfrage vor dem Entfernen.
 *
 * Gefragt wird in einem `ConfirmDialog`, nicht an der Stelle des Knopfes: eine
 * Rückfrage, die sich in die Zeile schiebt, macht sie im Moment des Klicks
 * länger, und alles darunter springt.
 */
type Asking = {
  readonly lesson: string;
  readonly what:
    { readonly kind: "lesson" } | { readonly kind: "stage"; readonly key: string };
};

/**
 * Der Grund, warum hier nichts zu entfernen ist – als Tooltip am blassen Knopf.
 *
 * Einmal, für Lektion und Stufe: es ist derselbe Grund, und zwei Formulierungen
 * dafür wären zwei Sätze, die auseinanderlaufen können.
 */
const GEUEBT = "Geübt – nicht mehr zu entfernen";

/** Kennung des einklappbaren Teils der Lektionskarte, für `aria-controls`. */
const LEKTION_INHALT = "lektion-inhalt";

export function LessonDetail() {
  const slug = useCourseSlug();
  const { lessonKey } = useParams();
  const {
    bundle,
    publishedBundle,
    setBundle,
    problems,
    publishedStageKeys,
    lockedStageKeys,
    readOnly,
  } = useDraft();
  useAnchorFocus();

  // Welche Rückfrage offensteht - und zu welcher Lektion. Der Schlüssel gehört
  // dazu: beim Wechsel auf eine andere Lektion bleibt dies dieselbe Komponente,
  // und eine offene Rückfrage gehörte sonst plötzlich zu einem anderen Ding.
  const [asking, setAsking] = useState<Asking | null>(null);
  // Welche Stufe gerade gezogen wird und über welcher sie schwebt. Wie in der
  // Leiste bei den Lektionen – dasselbe Verhalten, damit Ziehen hier nichts
  // anderes bedeutet als dort.
  const [dragging, setDragging] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);

  // Welche Stufen aufgeklappt sind. Mehrere dürfen es sein: beim Redigieren
  // vergleicht man zwei Übungszeilen, und ein Akkordeon, das die eine zuklappt,
  // sobald man die andere öffnet, macht genau das unmöglich.
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  const [lessonOpen, setLessonOpen] = useState(false);
  const toggleStage = (key: string) =>
    setOpen((offen) => {
      const next = new Set(offen);
      if (!next.delete(key)) next.add(key);
      return next;
    });

  // Der Anker aus der Adresse. Eine Stufe, die ein Hinweis anspringt, klappt
  // sich von selbst auf – sonst führte der Weg auf eine zugeklappte Karte.
  const { hash } = useLocation();
  const ausAdresse = hash ? decodeURIComponent(hash.slice(1)) : "";

  const lesson = bundle?.lessons.find((l) => l.key === lessonKey);

  if (!bundle) return <NoDraft />;

  // Entfernt, aber inzwischen geübt: der einzige Fall, in dem eine fehlende
  // Lektion keine Umleitung verdient. Zwischen Löschen und Veröffentlichen
  // liegen Tage; hat in ihnen ein Kind zum ersten Mal daran geübt, sperrt der
  // Hinweis das Veröffentlichen – und ohne diesen Weg bliebe nur „Verwerfen“.
  const removed = removedButInUse(bundle, publishedBundle, lockedStageKeys);
  const zurueckzuholen = removed.lessons.find((l) => l.key === lessonKey);

  // Sonst: eine Lektion, die es nicht gibt, ist fast immer ein alter Link – und
  // dann will man zur ersten, nicht zu einer Fehlermeldung.
  if (!lesson) {
    if (!zurueckzuholen || readOnly || !publishedBundle) {
      return <Navigate to={coursePath(slug, "lektionen")} replace />;
    }
    return (
      <div className={styles.card}>
        <h1 className={styles.title}>{zurueckzuholen.title}</h1>
        <p>
          Diese Lektion ist im Entwurf entfernt. Inzwischen hat ein Kind daran geübt – an
          ihren Stufen hängen Sterne, deshalb lässt sich der Entwurf so nicht
          veröffentlichen.
        </p>
        <button
          type="button"
          className={styles.button}
          onClick={() => setBundle(restoreLesson(bundle, publishedBundle, lessonKey!))}
        >
          Lektion zurückholen
        </button>
      </div>
    );
  }

  const lessonLocked = lesson.stages.some((s) => lockedStageKeys.has(s.key));

  // Die Hinweise der Lektion selbst – die der Stufen stehen an der Stufe.
  const eigeneHinweise = problemsAtExcept(
    problems,
    `lessons.${lesson.key}`,
    lesson.stages.map((s) => `lessons.${lesson.key}.${s.key}`),
  );
  const fragt = asking?.lesson === lesson.key ? asking.what : null;

  // Tasten, die die Lektion führt, die aber in keiner Übungszeile vorkommen.
  const typed = new Set(lesson.stages.flatMap((s) => [...s.text]));
  const untyped = lesson.keys.filter((k) => !typed.has(k));

  // Zählt mit, was einen Grund gibt hineinzusehen. Der ungetippte Tastenvorrat
  // ist kein Fehler und kein `Problem`, aber zugeklappt fiele er sonst weg.
  const lektionHinweise = eigeneHinweise.length + (untyped.length > 0 ? 1 : 0);
  // Ein Anker in der Adresse zeigt auf ein Feld DIESER Karte – dann muss sie
  // offen sein, bevor `useAnchorFocus` das Feld sucht.
  const lektionOffen =
    lessonOpen ||
    ausAdresse === anchors.lessonKeys ||
    ausAdresse === anchors.lessonUppercase;

  const change = (part: Partial<Lesson>) =>
    setBundle({
      ...bundle,
      lessons: bundle.lessons.map((l) => (l.key === lesson.key ? { ...l, ...part } : l)),
    });

  const changeStage = (stageKey: string, part: Partial<Stage>) =>
    setBundle({
      ...bundle,
      lessons: bundle.lessons.map((l) =>
        l.key === lesson.key
          ? {
              ...l,
              stages: l.stages.map((s) => (s.key === stageKey ? { ...s, ...part } : s)),
            }
          : l,
      ),
    });

  /**
   * Eine leere Stufe an dieser Stelle einschieben.
   *
   * Vorher stand hier ein zweites `appendStage`, das dasselbe noch einmal baute
   * wie `insertStage` – bis auf die Stelle. Eine Stufe entsteht jetzt an einem
   * Ort, sonst driften Vorgabetitel und Startzeile auseinander.
   */
  const insertAt = (at: number) =>
    setBundle(insertStage(bundle, lesson.key, at, publishedStageKeys));

  return (
    <>
      <div className={styles.card}>
        <div className={`${own.detailHead} ${lektionOffen ? "" : own.detailHeadClosed}`}>
          {/* Der Titel der Lektion schaltet ihre Felder auf und zu – dieselbe
              Form wie bei den Stufen. Zugeklappt stehen die Stufen weiter oben
              auf dem Bildschirm, und darum geht es in dieser Liste. */}
          <button
            type="button"
            className={own.lessonToggle}
            aria-expanded={lektionOffen}
            aria-controls={LEKTION_INHALT}
            onClick={() => setLessonOpen((offen) => !offen)}
          >
            <span className={own.stageChevron} aria-hidden="true">
              {lektionOffen ? "▾" : "▸"}
            </span>
            <h1 className={styles.title}>
              {lesson.number}. {lesson.title}
            </h1>
            {/* Zugeklappt wären die Hinweise der Lektion unsichtbar. Die Zahl
                sagt, dass hier etwas liegt – der ungetippte Tastenvorrat zählt
                mit, er ist kein Fehler, aber einer der Gründe hineinzusehen. */}
            {lektionHinweise > 0 && (
              <span className={own.stageCount}>{lektionHinweise}</span>
            )}
          </button>
          <div className={own.tools}>
            {readOnly && (
              <span className={own.note}>
                Veröffentlichte Fassung – zum Ändern oben einen Entwurf anlegen
              </span>
            )}
            {!readOnly && (
              <>
                {lessonLocked ? (
                  <button
                    type="button"
                    className={own.removeOff}
                    aria-disabled="true"
                    title={GEUEBT}
                    aria-label={GEUEBT}
                  >
                    <TrashIcon />
                  </button>
                ) : (
                  <button
                    type="button"
                    className={own.remove}
                    title={`Lektion „${lesson.title}“ entfernen`}
                    aria-label={`Lektion „${lesson.title}“ entfernen`}
                    onClick={() =>
                      setAsking({ lesson: lesson.key, what: { kind: "lesson" } })
                    }
                  >
                    <TrashIcon />
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        {lektionOffen && (
          <div id={LEKTION_INHALT}>
            <div className={own.pairTitle}>
              <label>
                Titel
                <input
                  className={styles.field}
                  value={lesson.title}
                  readOnly={readOnly}
                  onChange={(e) => change({ title: e.target.value })}
                />
              </label>
              <label>
                Untertitel
                <input
                  className={styles.field}
                  value={lesson.subtitle}
                  readOnly={readOnly}
                  onChange={(e) => change({ subtitle: e.target.value })}
                />
              </label>
              {/* Die Farbe der Lektion war bisher nur im Assistenten zu setzen –
              ein Feld des Bundles ohne Oberfläche ist Code mit Umweg. */}
              <label>
                Farbe
                <input
                  type="color"
                  className={own.color}
                  aria-label="Farbe der Lektion"
                  value={lesson.color}
                  disabled={readOnly}
                  onChange={(e) => change({ color: e.target.value.toUpperCase() })}
                />
              </label>
            </div>

            {/* Zwei kurze Felder nebeneinander: einzeln über die volle Breite
            gestellt, drängten sie die Einleitung unter den Falz. */}
            <div className={own.pair}>
              {/* Ohne diese Auswahl wäre der Weg nur im Quellcode setzbar – und ein
              Bundle-Feld ohne Redaktionsoberfläche ist kein Inhalt, sondern Code
              mit Umweg. */}
              <label id={anchors.lessonUppercase}>
                Großbuchstaben entstehen mit
                <select
                  className={styles.field}
                  value={lesson.uppercaseWith ?? ""}
                  disabled={readOnly}
                  onChange={(e) =>
                    change(
                      e.target.value === ""
                        ? { uppercaseWith: undefined }
                        : { uppercaseWith: e.target.value as "shift" | "capsLock" },
                    )
                  }
                >
                  <option value="">egal – die Lektion prüft es nicht</option>
                  <option value="shift">Umschalttaste</option>
                  <option value="capsLock">Feststelltaste</option>
                </select>
                <span className={own.note}>
                  Ist etwas gewählt, zählt der andere Weg als Vertipper: der Buchstabe
                  stimmt, aber die Übung meint den anderen.
                </span>
              </label>

              <label id={anchors.lessonKeys}>
                Tasten dieser Lektion (mit Komma trennen, ␣ für die Leertaste)
                <input
                  className={`${styles.field} mono`}
                  value={formatKeys(lesson.keys)}
                  readOnly={readOnly}
                  onChange={(e) => change({ keys: parseKeys(e.target.value) })}
                />
              </label>
            </div>

            {/* Rot, obwohl es das Veröffentlichen nicht sperrt: erlaubt ist es –
            „Feststelltaste“ führt die ganze Grundreihe und tippt nur
            Großbuchstaben. Die häufigere Ursache ist aber ein Rest aus einer
            früheren Fassung, und der fiel als leiser Satz unter dem Feld
            niemandem auf. Der Text sagt deshalb ausdrücklich, dass es kein
            Fehler ist. */}
            {untyped.length > 0 && (
              <div className={`${styles.notice} ${styles.noticeError}`}>
                <strong className="mono">{formatKeys(untyped)}</strong> steht in den
                Tasten dieser Lektion, kommt aber in keinem Übungstext vor. Auf dem
                Tastaturbild ist die Taste eingefärbt, getippt wird sie nie.
                <span className={own.note}>
                  Kein Fehler – eine Lektion darf eine Taste führen, ohne sie zu üben.
                  Prüfen Sie, ob sie ein Rest aus einer früheren Fassung ist.
                </span>
              </div>
            )}

            <label className={own.block}>
              Einleitung
              <textarea
                className={`${styles.field} ${styles.multiline} ${own.intro}`}
                value={lesson.intro}
                readOnly={readOnly}
                onChange={(e) => change({ intro: e.target.value })}
              />
            </label>

            {/* Die Hinweise der Lektion selbst – die der Stufen stehen an der Stufe. */}
            <ProblemList problems={eigeneHinweise} />
          </div>
        )}
      </div>

      {/* Auch vor der ersten Stufe: eine neue soll überall hin können, nicht
          nur hinter etwas. Das deckt zugleich die Lektion ohne Stufen ab – dann
          ist dies das einzige Pluszeichen. */}
      {!readOnly && <AddStage onClick={() => insertAt(0)} />}

      {lesson.stages.map((stage, i) => {
        // Entfernen hängt am Geübtsein, nicht am Veröffentlichtsein.
        // `publishedStageKeys` wird hier trotzdem gebraucht: beim Einschieben
        // einer neuen Stufe, damit ein einmal vergebener Schlüssel nicht
        // wiederverwendet wird.
        const removable = !lockedStageKeys.has(stage.key);
        const notices = problemsAt(problems, `lessons.${lesson.key}.${stage.key}`);
        const anker = anchors.stage(stage.key);
        // Zugeklappt ist die Vorgabe – der Sinn der Liste ist, alle Stufen auf
        // einmal zu sehen. Offen ist, was angeklickt wurde ODER was gerade ein
        // Hinweis anspringt: der Anker steht in der Adresse, und die Stufe klappt
        // sich auf, bevor `useAnchorFocus` das Feld sucht. Andersherum fände der
        // Sprung ein Feld, das es noch nicht gibt.
        const offen = open.has(stage.key) || anker === ausAdresse;
        const inhaltId = `${anker}-inhalt`;
        return (
          <Fragment key={stage.key}>
            <div
              id={anker}
              data-stage-card=""
              onDragOver={(e) => {
                if (!dragging || readOnly) return;
                // Ohne preventDefault lehnt der Browser das Fallenlassen ab – der
                // Zeiger zeigt dann dauerhaft das Verbotsschild.
                e.preventDefault();
                e.dataTransfer.dropEffect = "move";
                setOver(stage.key);
              }}
              onDrop={(e) => {
                e.preventDefault();
                if (dragging && dragging !== stage.key) {
                  setBundle(moveStageTo(bundle, lesson.key, dragging, i));
                }
                setDragging(null);
                setOver(null);
              }}
              className={[
                styles.card,
                own.stage,
                dragging === stage.key ? own.stageDragging : "",
                over === stage.key && dragging !== stage.key ? own.stageOver : "",
              ].join(" ")}
            >
              <div className={own.stageHead}>
                {!readOnly && (
                  <button
                    type="button"
                    className={own.grip}
                    title="Ziehen zum Umsortieren – oder Alt + Pfeil hoch/runter"
                    aria-label={`Stufe ${stage.title} umsortieren`}
                    draggable
                    onDragStart={(e) => {
                      setDragging(stage.key);
                      // Ohne Nutzlast bricht Firefox das Ziehen sofort ab.
                      e.dataTransfer.setData("text/plain", stage.key);
                      e.dataTransfer.effectAllowed = "move";
                      // Sonst hinge am Zeiger der Griff statt der Stufe.
                      const karte = e.currentTarget.closest("[data-stage-card]");
                      if (karte instanceof HTMLElement) {
                        e.dataTransfer.setDragImage(karte, 24, 24);
                      }
                    }}
                    onDragEnd={() => {
                      setDragging(null);
                      setOver(null);
                    }}
                    // Ziehen allein wäre nur mit der Maus zu bedienen. Alt+Pfeil
                    // tut dasselbe von der Tastatur aus – wie in der Leiste.
                    onKeyDown={(e) => {
                      if (!e.altKey) return;
                      if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;
                      e.preventDefault();
                      setBundle(
                        moveStage(
                          bundle,
                          lesson.key,
                          stage.key,
                          e.key === "ArrowUp" ? -1 : 1,
                        ),
                      );
                    }}
                  >
                    ⠿
                  </button>
                )}

                {/* Die Überschrift IST der Titel der Stufe. Geändert wird er im
                    Feld darin – zwei schreibbare Stellen für denselben Wert
                    wären eine Frage, welche gilt. */}
                <button
                  type="button"
                  className={own.stageToggle}
                  aria-expanded={offen}
                  aria-controls={inhaltId}
                  onClick={() => toggleStage(stage.key)}
                >
                  <span className={own.stageChevron} aria-hidden="true">
                    {offen ? "▾" : "▸"}
                  </span>
                  <span className={own.stageName}>{stage.title || "Ohne Titel"}</span>
                  {/* Zugeklappt wären die Hinweise der Stufe unsichtbar. Die Zahl
                      sagt, dass hier etwas liegt – wie in der Leiste bei den
                      Lektionen. */}
                  {notices.length > 0 && (
                    <span className={own.stageCount}>{notices.length}</span>
                  )}
                </button>

                {!readOnly &&
                  (removable ? (
                    <button
                      type="button"
                      className={own.remove}
                      title={`Stufe „${stage.title}“ entfernen`}
                      aria-label={`Stufe „${stage.title}“ entfernen`}
                      onClick={() => {
                        // Eine gerade angelegte Stufe wieder wegzuklicken soll
                        // nicht so schwer sein wie das Entfernen einer Übung, an
                        // der jemand eine Stunde gesessen hat.
                        if (stage.title === "Neue Stufe") {
                          setBundle(removeStage(bundle, lesson.key, stage.key));
                        } else {
                          setAsking({
                            lesson: lesson.key,
                            what: { kind: "stage", key: stage.key },
                          });
                        }
                      }}
                    >
                      <TrashIcon />
                    </button>
                  ) : (
                    <button
                      type="button"
                      className={own.removeOff}
                      aria-disabled="true"
                      title={GEUEBT}
                      aria-label={GEUEBT}
                    >
                      <TrashIcon />
                    </button>
                  ))}
              </div>

              {offen && (
                <div id={inhaltId} className={own.stageDetails}>
                  <label className={own.block}>
                    Titel der Stufe
                    <input
                      className={styles.field}
                      value={stage.title}
                      readOnly={readOnly}
                      aria-label={`Titel ${stage.key}`}
                      onChange={(e) => changeStage(stage.key, { title: e.target.value })}
                    />
                  </label>

                  <div className={own.stageBody}>
                    <div className={own.stageFields}>
                      {/* Beschriftet, weil drei Kästen untereinander nicht sagen,
                          welcher was ist. Vorher stand die Auskunft nur im
                          aria-label – also für alle unsichtbar, die sehen können. */}
                      <label className={own.block}>
                        Hinweis an das Kind – steht über der Übung
                        <input
                          className={styles.field}
                          value={stage.hint}
                          readOnly={readOnly}
                          aria-label={`Hinweis ${stage.key}`}
                          onChange={(e) =>
                            changeStage(stage.key, { hint: e.target.value })
                          }
                        />
                      </label>
                      <label className={own.block}>
                        Übungstext – die Zeile, die getippt wird
                        <textarea
                          className={`${styles.field} ${styles.multiline} ${own.stageText}`}
                          value={stage.text}
                          readOnly={readOnly}
                          aria-label={`Übungstext ${stage.key}`}
                          onChange={(e) =>
                            changeStage(stage.key, { text: e.target.value })
                          }
                        />
                      </label>
                    </div>
                    <div className={own.previewColumn}>
                      <span className={own.previewLabel}>So sieht es das Kind</span>
                      <StagePreview bundle={bundle} lesson={lesson} stage={stage} />
                    </div>
                  </div>

                  <ProblemList problems={notices} withoutPath />
                </div>
              )}
            </div>
            {!readOnly && <AddStage onClick={() => insertAt(i + 1)} />}
          </Fragment>
        );
      })}

      {!readOnly &&
        publishedBundle &&
        removed.stages
          .filter((stage) =>
            publishedBundle.lessons
              .find((l) => l.key === lesson.key)
              ?.stages.some((s) => s.key === stage.key),
          )
          .map((stage) => (
            <div
              key={stage.key}
              id={anchors.stage(stage.key)}
              className={`${styles.card} ${styles.spaced}`}
            >
              <p className={own.note}>
                Die Stufe „{stage.title}“ ist entfernt, aber ein Kind hat daran geübt.
              </p>
              <button
                type="button"
                className={styles.button}
                onClick={() =>
                  setBundle(restoreStage(bundle, publishedBundle, lesson.key, stage.key))
                }
              >
                Stufe zurückholen
              </button>
            </div>
          ))}

      <ConfirmDialog
        open={fragt !== null}
        title={fragt?.kind === "lesson" ? "Lektion entfernen?" : "Stufe entfernen?"}
        confirmLabel="Entfernen"
        onCancel={() => setAsking(null)}
        onConfirm={() => {
          if (!fragt) return;
          setAsking(null);
          setBundle(
            fragt.kind === "lesson"
              ? removeLesson(bundle, lesson.key)
              : removeStage(bundle, lesson.key, fragt.key),
          );
        }}
      >
        {fragt?.kind === "lesson" ? (
          <>
            „{lesson.title}“ mit {lesson.stages.length}{" "}
            {lesson.stages.length === 1 ? "Stufe" : "Stufen"}. Solange der Entwurf nicht
            veröffentlicht ist, lässt sich das rückgängig machen – mit „Verwerfen“ in der
            Fassungsleiste.
          </>
        ) : (
          <>„{lesson.stages.find((s) => s.key === fragt?.key)?.title}“</>
        )}
      </ConfirmDialog>
    </>
  );
}

/**
 * Das Pluszeichen zwischen zwei Stufen.
 *
 * Blass, bis man hinzeigt – dann wächst der Hinweistext heraus. Vorher stand an
 * jeder Stufe ein Knopf „Stufe darunter einschieben“: bei sieben Stufen
 * siebenmal derselbe Satz, und wo die neue Stufe landet, war nur aus dem Wort
 * „darunter“ zu erschließen. Das Pluszeichen sitzt an dieser Stelle.
 */
function AddStage({ onClick }: { readonly onClick: () => void }) {
  return (
    <div className={own.addRow}>
      <button
        type="button"
        className={own.add}
        onClick={onClick}
        title="Stufe hinzufügen"
        aria-label="Stufe hinzufügen"
      >
        <span className={own.addPlus} aria-hidden="true">
          +
        </span>
        <span className={own.addText} aria-hidden="true">
          Stufe hinzufügen
        </span>
      </button>
    </div>
  );
}
